/**
 * Scroll reveal for elements carrying the `reveal` class.
 *
 * critical.css hides `.reveal` elements only while `html.js` is set and the
 * element has no `data-revealed` attribute, so a JS failure leaves content
 * visible rather than blank.
 */
(function () {
  var SELECTOR = '.reveal:not([data-revealed])';

  function revealAll(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(function (el) {
      el.setAttribute('data-revealed', '');
    });
  }

  // No IntersectionObserver (or merchant turned reveals off): show everything.
  if (!('IntersectionObserver' in window)) {
    revealAll();
    return;
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.setAttribute('data-revealed', '');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }
  );

  function observe(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(function (el) {
      observer.observe(el);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    observe();
  });

  if (document.readyState !== 'loading') observe();

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    observe(event.target);
  });
})();

/**
 * Button label slide.
 *
 * The "Slide label up" hover effect needs two copies of the label stacked
 * vertically inside a masked track. Rather than requiring every button in the
 * theme to be authored that way, the markup is added here at runtime.
 *
 * Only plain-text buttons are enhanced — anything containing an icon or other
 * markup is left exactly as authored.
 */
(function () {
  function enhance(button) {
    if (button.dataset.labelEnhanced) return;

    // Text-only buttons: no element children, and something to show.
    const label = button.textContent.trim();
    if (!label || button.children.length > 0) return;

    button.dataset.labelEnhanced = 'true';

    const mask = document.createElement('span');
    mask.className = 'button__label';

    const track = document.createElement('span');
    track.className = 'button__label-track';

    const first = document.createElement('span');
    first.textContent = label;

    const second = document.createElement('span');
    second.textContent = label;
    second.setAttribute('aria-hidden', 'true');

    track.append(first, second);
    mask.appendChild(track);

    button.textContent = '';
    button.appendChild(mask);
  }

  function enhanceAll(root) {
    (root || document).querySelectorAll('.button').forEach(enhance);
  }

  if (document.readyState !== 'loading') enhanceAll();
  document.addEventListener('DOMContentLoaded', () => enhanceAll());
  document.addEventListener('shopify:section:load', (event) => enhanceAll(event.target));
})();

/**
 * Repeats a heading until it fills its row, then loops it seamlessly.
 *
 * Lives here rather than in a section so more than one section can offer a
 * scrolling heading — a section's own script only ships when that section is
 * on the page.
 */
/**
   * Repeats the heading until it fills the row, then loops it seamlessly.
   *
   * The travel is one copy's width, so the row only stays unbroken while the
   * copies trailing the first can cover the viewport between them — which is
   * why the count comes from the measured heading rather than being fixed.
   */
  class MarqueeHeading extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      this.source = this.querySelector('[data-marquee-copy-source]');
      if (!this.track || !this.source) return;

      // Web fonts change how wide the heading is, so the first measurement
      // waits for them rather than timing the loop against a fallback face.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.build());
      } else {
        this.build();
      }

      this.onResize = () => this.build();
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    build() {
      const width = this.source.getBoundingClientRect().width;
      if (!width) return;

      const step = width + 24;
      const copies = Math.max(2, Math.ceil(window.innerWidth / step) + 1);
      if (this.copies === copies) return;
      this.copies = copies;

      this.track.querySelectorAll('[data-marquee-copy]').forEach((node) => node.remove());

      for (let i = 1; i < copies; i += 1) {
        const clone = this.source.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-marquee-copy', '');
        clone.removeAttribute('data-marquee-copy-source');
        this.track.appendChild(clone);
      }

      this.style.setProperty('--marquee-copies', copies);
      this.style.setProperty('--marquee-duration', `${(step * copies) / 90}s`);

      // Restart rather than re-time: changing an animation's duration part way
      // through keeps its elapsed time and lands it elsewhere in the loop.
      this.track.style.animation = 'none';
      void this.track.offsetWidth;
      this.track.style.animation = '';

      this.setAttribute('data-ready', '');
    }
  }

  if (!customElements.get('marquee-heading')) {
    customElements.define('marquee-heading', MarqueeHeading);
  }
